import { useState } from 'react';

import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import { ReportDownloadButton } from './components';
import { BUBBLE_TITLES } from './constants';
import * as Styled from './ReportPanel.style';
import ReportSection from './ReportSection';
import { transformMarketAnalysisToSections } from '../../utils/marketReportUtils';
import { formatSectionTitle, getIconForSection } from '../../utils/reportUtils';

/**
 * Normalizes any report data format into standardized sections array
 *
 * This is the ONLY place where format detection happens.
 * To add a new report type:
 * 1. Add detection logic (check for unique field)
 * 2. Add normalization logic (convert to sections array)
 *
 * @param {object} data - Report data in any format
 * @param {string} iconVariant - Icon variant ('regular' or 'rounded')
 * @returns {Array<Section>} Standardized sections array
 *
 * @typedef {object} Section
 * @property {string} id - Unique section identifier
 * @property {string} title - Section title
 * @property {any} content - Section content (format depends on component type)
 * @property {string} component - Component type ('markdown', 'similar-businesses', etc.)
 * @property {ReactElement} icon - Section icon
 * @property {string} [cost] - Optional cost range for cost reports
 */
function normalizeSections(data, iconVariant = 'regular') {
  if (!data) return [];

  // ==========================================
  // Format 1: Business Names
  // Structure: { report: { sections: [...] } }
  // ==========================================
  if (data.report?.sections && Array.isArray(data.report.sections)) {
    return data.report.sections.map((section) => ({
      id: section.id,
      title: section.title,
      content: section.content,
      component: section.component || 'markdown',
      icon: section.icon || getIconForSection(section.id, iconVariant),
      subsections: section.subsections || []
    }));
  }

  // ==========================================
  // Format 2: Business Ideas
  // Structure: { businessPlan: { field1: "...", field2: "..." } }
  // ==========================================
  if (data.businessPlan && typeof data.businessPlan === 'object') {
    return Object.entries(data.businessPlan)
      .filter(([, value]) => value) // Remove empty values
      .map(([key, value]) => ({
        id: key,
        title: value.title || formatSectionTitle(key),
        content: value,
        component: value.component || 'markdown',
        icon: getIconForSection(key, iconVariant),
        subsections: value.subsections || []
      }));
  }

  // ==========================================
  // Format 3: Cost Report
  // Structure: { sections: { field1: {content, cost}, field2: {...} } }
  // ==========================================
  if (data.sections && !Array.isArray(data.sections) && typeof data.sections === 'object') {
    return Object.entries(data.sections)
      .filter(([, value]) => value)
      .map(([key, value]) => ({
        id: key,
        title: value.title || formatSectionTitle(key),
        content: typeof value === 'string' ? value : value.content,
        cost: value.cost, // Generic cost field
        component: value.component || 'markdown',
        icon: getIconForSection(key, iconVariant),
        subsections: value.subsections || []
      }));
  }

  // ==========================================
  // Format 4: Market Analysis
  // Structure: { executiveSummary: {...}, marketOverview: {...}, ... }
  // ==========================================
  if (data.executiveSummary || data.marketOverview || data.marketTrends) {
    // Use existing transformation function
    return transformMarketAnalysisToSections(data);
  }

  // ==========================================
  // Unknown format - log warning
  // ==========================================
  console.warn('ReportPanel: Unknown data format. Keys found:', Object.keys(data));
  return [];
}

function mergeSubsection(subsection, config) {
  return {
    ...subsection,
    title: subsection.title || config?.title,
    component: subsection.component || config?.component || 'markdown',
    content: subsection.content || config?.content,
    ...(config?.isBubble && { isBubble: config.isBubble }),
    ...(config?.disclaimer && { disclaimer: config.disclaimer }),
    ...(config?.showDisclaimer && { showDisclaimer: config.showDisclaimer })
  };
}

function mergeSection(section, config) {
  const subsections = section.subsections
    ?.map((subsection) => {
      // Find matching subsection
      const subsectionConfig = config.subsections?.find((s) => s.id === subsection.id);
      return mergeSubsection(subsection, subsectionConfig);
    })
    .filter(Boolean);

  return {
    ...section,
    title: section.title || config.title,
    component: section.component || config.component,
    // Only use config.icon if it's a React element (not a string or null)
    icon:
      section.icon || (config.icon && typeof config.icon === 'object' ? config.icon : undefined),
    ...(config?.isBubble && { isBubble: config.isBubble }),
    ...(config?.disclaimer && { disclaimer: config.disclaimer }),
    ...(config?.showDisclaimer && { showDisclaimer: config.showDisclaimer }),
    ...(subsections && { subsections })
  };
}

/**
 * Report panel component for displaying detailed business reports
 *
 * Generic component that can render any report type by normalizing the data
 * into a standardized sections array format.
 *
 * Supports multiple report types:
 * - Business Ideas: { businessPlan: { field1: "...", ... } }
 * - Business Names: { report: { sections: [...] } }
 * - Cost Reports: { sections: { field1: {content, cost}, ... } }
 * - Market Analysis: { executiveSummary: {...}, marketOverview: {...}, ... }
 *
 * @param {object} props - Component props
 * @param {object} props.reportData - Report data in any supported format
 * @param {Array<object>} [props.sectionsConfig] - Optional config for ordering/filtering sections
 * @param {string} [props.iconVariant='regular'] - Icon variant: 'regular' or 'rounded'
 * @param {Function} [props.onDownloadReport] - Handler for download report action (optional)
 * @param {boolean} [props.loading=false] - Whether content is loading
 * @param {string} [props.emptyMessage='Select an item to view detailed report'] - Message when no item selected
 * @component
 */
function ReportPanel({
  reportData,
  sectionsConfig,
  iconVariant = 'regular',
  onDownloadReport,
  loading = false,
  emptyMessage = 'Select an item to view detailed report'
}) {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const showDownload = Boolean(onDownloadReport);

  const handleDownloadReport = async () => {
    if (downloadLoading || loading || !onDownloadReport) {
      return;
    }

    setDownloadLoading(true);
    try {
      if (reportData) {
        await onDownloadReport(reportData.id);
      }
    } catch (error) {
      console.error('Failed to download report:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Empty state
  if (!reportData) {
    return (
      <Styled.MainContent>
        <Styled.ContentHeader>
          <Styled.ContentTitle color="secondary" size="sm">
            {emptyMessage}
          </Styled.ContentTitle>
        </Styled.ContentHeader>
      </Styled.MainContent>
    );
  }

  // Step 1: Normalize data to sections array
  let sections = normalizeSections(reportData, iconVariant);

  console.log('sections', sections);

  // Step 2: Apply sectionsConfig if provided (optional)
  if (sectionsConfig && sectionsConfig.length > 0) {
    sections = sectionsConfig
      .map((config) => {
        // Find matching section
        const section = sections.find((s) => s.id === config.id);
        if (!section) return null;
        // Merge config with section (config overrides)
        return mergeSection(section, config);
      })
      .filter(Boolean); // Remove nulls
  }

  // Step 3: Render!
  return (
    <Styled.MainContent>
      <Styled.ContentHeader>
        <Text color="secondary" size="sm">
          Detailed report
        </Text>
        <Styled.ContentSubHeader>
          <Box>
            <Text color="primary" mb={1} size="xl" weight="medium">
              {reportData.title}
            </Text>
            {reportData.description && (
              <Text color="secondary" size="md">
                {reportData.description}
              </Text>
            )}
          </Box>
          {showDownload && (
            <ReportDownloadButton
              disabled={downloadLoading || loading}
              loading={downloadLoading}
              reportTitle={reportData.title}
              size="small"
              onClick={handleDownloadReport}
            />
          )}
        </Styled.ContentSubHeader>
      </Styled.ContentHeader>

      <Styled.ContentBody>
        {sections.map((section) => (
          <Box key={section.id} id={`section-${section.id}`}>
            <ReportSection
              component={section.component || 'markdown'}
              content={section.content}
              costRange={section.cost}
              disclaimer={section.disclaimer}
              icon={section.icon}
              isBubble={BUBBLE_TITLES.includes(section.title?.toLowerCase() || '')}
              showDisclaimer={section.showDisclaimer}
              subsections={section.subsections}
              title={section.title}
            />
          </Box>
        ))}

        {showDownload && (
          <Styled.FooterSectionContent>
            <Text size="md" weight="medium">
              Ready to take the next step?
            </Text>
            <Text color="secondary" mb={2} size="sm">
              Download this comprehensive business report as a PDF for easy reference and future
              planning.
            </Text>
            <ReportDownloadButton
              disabled={downloadLoading || loading}
              loading={downloadLoading}
              reportTitle={reportData.title}
              size="medium"
              onClick={handleDownloadReport}
            />
          </Styled.FooterSectionContent>
        )}
      </Styled.ContentBody>
    </Styled.MainContent>
  );
}

ReportPanel.propTypes = {
  reportData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string
    // Note: Can contain any of the 4 data formats internally
  }).isRequired,
  sectionsConfig: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      component: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      isBubble: PropTypes.bool,
      disclaimer: PropTypes.string,
      showDisclaimer: PropTypes.bool
    })
  ),
  iconVariant: PropTypes.oneOf(['regular', 'rounded']),
  onDownloadReport: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string
};

export default ReportPanel;
